import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { exchangeMyobAccessToken } from '@/features/auth/api'
import { consumeMyobOAuthState } from '@/features/platform/myobOAuthState'
import PlatformConnectionScreen, { type PlatformConnectionStatus } from './components/PlatformConnectionScreen'
import useAutoCloseOnSuccess from './useAutoCloseOnSuccess'

export default function MyobCallbackPage() {
  const [searchParams] = useSearchParams()
  const exchangeStartedRef = useRef(false)
  const [status, setStatus] = useState<PlatformConnectionStatus>('connecting')
  const [errorMessage, setErrorMessage] = useState<string>()

  useEffect(() => {
    if (exchangeStartedRef.current) return
    exchangeStartedRef.current = true

    const oauthAttempt = consumeMyobOAuthState(searchParams.get('state'))
    if (!oauthAttempt) {
      setStatus('error')
      setErrorMessage('This MYOB authorization request is invalid or has expired. Please start the connection again.')
      return
    }

    const oauthError = searchParams.get('error')
    const oauthErrorDescription = searchParams.get('error_description')
    if (oauthError) {
      setStatus('error')
      setErrorMessage(oauthErrorDescription || 'MYOB authorization was not completed.')
      return
    }

    const code = searchParams.get('code')
    if (!code) {
      setStatus('error')
      setErrorMessage('The MYOB callback is missing required authorization details.')
      return
    }

    void exchangeMyobAccessToken({
      code,
      platformId: oauthAttempt.platformId,
      businessId: searchParams.get('businessId') || undefined,
      businessName: searchParams.get('businessName') || undefined,
    })
      .then(() => setStatus('success'))
      .catch((error: unknown) => {
        setStatus('error')
        setErrorMessage(error instanceof Error && error.message ? error.message : 'Unable to complete the MYOB connection.')
      })
  }, [searchParams])

  useAutoCloseOnSuccess(status)

  return <PlatformConnectionScreen platformName="MYOB" status={status} errorMessage={errorMessage} />
}
